/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2025 (original work) Open Assessment Technologies SA
 *
 */

import request, { getEndpointUrl, jwtTokenHandler } from '@/core/apiRequest/apiRequest';
import { parseJwtPayload } from 'core/jwt/jwtToken';
import config from '@/config';
import baseRequest from 'core/fetchRequest';
import { getSubdomain } from '../helpers/domainHelper';
import router from '@/core/router';
import * as sessionStorageHelper from '@/helpers/sessionStorageHelper.js';
import * as analyticsService from '@/services/analyticsService';
import { userIntegrationConfiguration } from '@/modules/users/stores/userIntegrationConfiguration.store';
import explorerStore from '@/modules/explorer/stores/explorer.store';
import eventEmitter from '@/core/eventEmitter';
import { configurationStore } from '@/stores/tenantConfiguration.store';
import { log } from '@/core/utils';

/**
 * @typedef User
 * @property {string[]} roles
 */

/**
 * Updates access tokens in store
 * @param {{accessToken: string, refreshToken: string}} tokens
 * @returns {[]}
 */
export const storeTokens = async ({ accessToken, refreshToken }) =>
    await Promise.all([jwtTokenHandler.storeRefreshToken(refreshToken), jwtTokenHandler.storeAccessToken(accessToken)]);

/**
 * Returns the tenant ID from the stored access token
 * @async
 * @returns {string|null}
 */
export const getTenantId = async () => {
    const token = await jwtTokenHandler.getToken();
    return parseJwtPayload(token).tenant_id || null;
};

/**
 * Switch to a working profile. Gets new accesss and refresh tokens for this working profile and stores them.
 * This is like loging in again, but without the need of the user to enter their credentials.
 * After getting the new token you should update the user info (and most times, redirect to the home page)
 * @param {string} workingProfileName
 * @async
 * @returns {Promise<void>}
 */
export const getWorkingProfileToken = async workingProfileName => {
    const formData = new FormData();

    const currentRefreshToken = await jwtTokenHandler.getRefreshToken();
    formData.append('grant_type', 'client_credentials');
    formData.append('client_assertion', currentRefreshToken);
    formData.append('client_assertion_type', 'urn:ietf:params:oauth:client-assertion-type:jwt-bearer');

    const response = await baseRequest(
        getEndpointUrl(
            'login',
            {},
            {
                profile: workingProfileName,
                'with-refresh-token': true // we want the refresh token back too
            },
            config.baseUrls.authServer
        ),
        { method: 'POST', body: formData }
    );

    const { access_token: accessToken, refresh_token: refreshToken } = response;
    storeTokens({ accessToken, refreshToken });
};
/**
 * Gets the user data from the server. This endpoint is used to validate the user when he logs in.
 * @async
 * @returns {Promise<User|null>} The user object or null if the user is not logged in
 */
export const getCurrentUser = async () => {
    const currentUser = await request(getEndpointUrl('userCurrent', {}, {}), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    return currentUser;
};

/**
 * Returns authorized user
 * @returns {Promise<User|null>}
 */
export const getUserTokenData = async () => {
    const token = await jwtTokenHandler.getToken();
    const jwtObject = parseJwtPayload(token) || {};

    const { hierarchy, roles } = jwtObject;
    return {
        userId: jwtObject.sub,
        roles,
        hierarchy
    };
};

/**
 * Sign in proctoring and saves access tokens
 * @param {{username: string, password: string, clientId: string}} credentials
 * @returns {void}
 */
export const login = async ({ username, password, clientId }) => {
    const formData = new FormData();

    formData.append('grant_type', 'password');
    formData.append('client_id', clientId);
    formData.append('username', username.trim());
    formData.append('password', password);

    const response = await baseRequest(getEndpointUrl('login', {}, {}, config.baseUrls.authServer), {
        method: 'POST',
        body: formData
    });

    const { access_token: accessToken, refresh_token: refreshToken } = response;

    storeTokens({ accessToken, refreshToken });

    clearSessionLogin(); // in case there was a previous session login, we remove the flag in sessionStorage
};

/**
 * Sign in proctoring and saves access tokens
 * @param {{username: string, password: string, clientId: string}} credentials
 * @returns {void}
 */
export const sessionLogin = async ({ username, password, clientId }) => {
    const formData = new FormData();

    formData.append('grant_type', 'password');
    formData.append('client_id', clientId);
    formData.append('username', username.trim());
    formData.append('password', password);

    const response = await baseRequest(getEndpointUrl('sessionLogin', {}, {}, config.baseUrls.authServer), {
        method: 'POST',
        body: formData
    });

    const { access_token: accessToken, refresh_token: refreshToken } = response;

    storeTokens({ accessToken, refreshToken });

    setSessionLogin(); // sets a flag in sessionStorage to know we should redirect to the session-login screen if the user logs out
};

/**
 * Redirect to Auth Server's SSO-Login URL, which will handle remaining SSO Auth flow
 * @param {{clientId: string, providerId: string}} params
 */
export const ssoLogin = params => {
    const authServerSSOLoginUrl = getEndpointUrl('ssoLogin', params, {}, config.baseUrls.authServer);
    router.navigateOut(authServerSSOLoginUrl);
};

/**
 * @typedef TenantData
 * @property {string} tenantId
 * @property {string} portalClientId
 * @property {string} msClientId
/**
 * Gets an array of tenant metadata from Tenant API
 * @returns {TenantData}
 */
export const getTenant = async () => {
    const subdomain = getSubdomain();
    const type = 'portal';
    return await baseRequest(getEndpointUrl('subdomain', { type, subdomain }, {}, config.baseUrls.authServer), {});
};

/**
 * Key to save preferences about the session login in sessionStorage. See `Session login` in the REAMDE
 */
const SESSION_LOGIN_KEY = 'isSessionLogin';

/**
 * Set the SESSION_LOGIN_KEY to true in the browser sessionStorage
 */
export function setSessionLogin() {
    sessionStorageHelper.setItem(SESSION_LOGIN_KEY, true);
}

export function isSessionLogin() {
    return sessionStorageHelper.getItem(SESSION_LOGIN_KEY);
}
function clearSessionLogin() {
    sessionStorageHelper.removeItem(SESSION_LOGIN_KEY);
}

async function logoutAuthoring() {
    try {
        if (!configurationStore.getAuthoringLogoutUrl()) {
            return;
        }
        await fetch(configurationStore.getAuthoringLogoutUrl(), {
            method: 'GET',
            credentials: 'include',
            redirect: 'manual'
        });
    } catch (error) {
        log.error('Authoring logout failed', error);
    }
}

/**
 * Logs the user out and redirects to the login screen.
 * It also removes any session-related user information and analytics objects.
 */
export async function logout() {
    await jwtTokenHandler.clearStore();
    explorerStore.reset();
    userIntegrationConfiguration.reset();
    analyticsService.deactivate();
    await logoutAuthoring();

    if (isSessionLogin()) {
        // to understand session login, there is a section in the README
        clearSessionLogin();
        router.redirect(config.routes.login.session);
    } else {
        router.redirect(config.routes.login.main);
    }

    eventEmitter.dispatchEvent(new CustomEvent('logout'));
}
